import { Link } from 'react-router'
import { Button } from '../../ui/button'
import { Icon } from '../../ui/icon'

interface FinalScreenProps {
	onBack: () => void
	data: Record<string, any>
}

export function FinalScreen({ onBack, data }: FinalScreenProps) {
	const getPlanName = (planId: string) => {
		const plans: Record<string, string> = {
			ceibo: 'Plan Ceibo',
			jacaranda: 'Plan Jacarandá',
			roble: 'Plan Roble',
			algarrobo: 'Plan Algarrobo',
		}
		return plans[planId] || 'Plan'
	}

	const getPlanBadge = (planId: string) => {
		const badges: Record<string, string> = {
			ceibo: 'Responsabilidad civil',
			jacaranda: 'Terceros',
			roble: 'Terceros full',
			algarrobo: 'Franquicia',
		}
		return badges[planId] || ''
	}

	return (
		<div className="flex w-full flex-col gap-6 pb-20 md:gap-8 md:pb-0">
			{/* Success message */}
			<div className="flex flex-col items-center gap-2 text-center">
				<h2 className="font-parkinsans text-[24px] leading-8 font-medium text-blue-800 md:text-[32px] md:leading-10">
					¡Recibimos tu solicitud!
				</h2>
				<p className="font-parkinsans text-sm leading-6 font-normal text-neutral-900 md:text-base md:leading-[30px]">
					Un asesor se pondrá en contacto para finalizar el proceso.
				</p>
			</div>

			{/* Information card */}
			<div className="flex flex-col gap-4 rounded-xl border border-neutral-300 bg-white p-4 md:p-6">
				{/* Plan header with download link */}
				<div className="flex flex-col items-start justify-between gap-3 border-b border-neutral-200 pb-4 md:flex-row md:items-center">
					<div className="flex items-center gap-2">
						<Icon name="natural-food" className="size-5 text-green-500" />
						<p className="font-parkinsans text-base leading-5 font-medium text-blue-800">
							{getPlanName(data.selectedPlan)}
						</p>
						<p className="font-parkinsans text-sm leading-5 font-normal text-neutral-700">
							{getPlanBadge(data.selectedPlan)}
						</p>
					</div>
					<button
						type="button"
						className="font-parkinsans flex items-center gap-2 text-sm leading-5 font-normal text-blue-500 underline transition-colors hover:text-blue-600"
					>
						Cobertura preseleccionada
						<Icon name="download" className="size-4" />
					</button>
				</div>

				{/* Data grid - single column on mobile, 2 on desktop */}
				<div className="grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-x-8 md:gap-y-3">
					{/* Column 1 */}
					<div className="flex flex-col gap-3">
						<div className="flex justify-between">
							<span className="font-parkinsans text-sm leading-5 font-normal text-neutral-700">
								Nombre
							</span>
							<span className="font-parkinsans text-sm leading-5 font-normal text-neutral-900">
								{data.fullName?.split(' ')[0] || 'Apellido'}
							</span>
						</div>
						<div className="flex justify-between">
							<span className="font-parkinsans text-sm leading-5 font-normal text-neutral-700">
								Apellido
							</span>
							<span className="font-parkinsans text-sm leading-5 font-normal text-neutral-900">
								{data.fullName?.split(' ').slice(1).join(' ') || 'Apellido'}
							</span>
						</div>
						<div className="flex justify-between">
							<span className="font-parkinsans text-sm leading-5 font-normal text-neutral-700">
								Teléfono
							</span>
							<span className="font-parkinsans text-sm leading-5 font-normal text-neutral-900">
								{data.phone || 'Mail'}
							</span>
						</div>
						<div className="flex justify-between">
							<span className="font-parkinsans text-sm leading-5 font-normal text-neutral-700">
								Mail
							</span>
							<span className="font-parkinsans text-sm leading-5 font-normal text-neutral-900">
								{data.email || 'Mail'}
							</span>
						</div>
					</div>

					{/* Column 2 */}
					<div className="flex flex-col gap-3">
						<div className="flex justify-between">
							<span className="font-parkinsans text-sm leading-5 font-normal text-neutral-700">
								Fiat
							</span>
							<span className="font-parkinsans text-sm leading-5 font-normal text-neutral-900">
								{data.carBrand || 'Fiat'}
							</span>
						</div>
						<div className="flex justify-between">
							<span className="font-parkinsans text-sm leading-5 font-normal text-neutral-700">
								Pulse
							</span>
							<span className="font-parkinsans text-sm leading-5 font-normal text-neutral-900">
								{data.carModel || 'Pulse'}
							</span>
						</div>
						<div className="flex justify-between">
							<span className="font-parkinsans text-sm leading-5 font-normal text-neutral-700">
								Impetus
							</span>
							<span className="font-parkinsans text-sm leading-5 font-normal text-neutral-900">
								{data.carVersion || 'Impetus'}
							</span>
						</div>
						<div className="flex justify-between">
							<span className="font-parkinsans text-sm leading-5 font-normal text-neutral-700">
								2025
							</span>
							<span className="font-parkinsans text-sm leading-5 font-normal text-neutral-900">
								{data.carYear || '2025'}
							</span>
						</div>
					</div>
				</div>
			</div>

			{/* Environmental impact message */}
			<div className="flex flex-col items-start gap-3 rounded-xl bg-green-50 p-4 md:flex-row">
				<div className="shrink-0">
					<Icon name="natural-food" className="size-6 text-green-500" />
				</div>
				<div className="flex flex-col gap-1">
					<p className="font-parkinsans text-sm leading-6 font-medium text-blue-800 md:text-base">
						Gracias por ser parte de este propósito
					</p>
					<p className="font-parkinsans text-xs leading-5 font-normal text-neutral-900 md:text-sm">
						Con la contratación de tu póliza estarás aportando a una acción de
						impacto ambiental.
					</p>
				</div>
			</div>

			{/* WhatsApp contact section */}
			<div className="flex flex-col items-start gap-3 md:flex-row md:items-center md:gap-4">
				<Button
					variant="success"
					appearance="solid"
					size="m"
					className="w-full gap-2 rounded-lg px-4 py-2 md:w-auto"
					onClick={() => window.open('https://wa.me/5491123456789', '_blank')}
				>
					<Icon name="envelope-closed" className="size-4" />
					Contacto
				</Button>
				<p className="font-parkinsans text-xs leading-5 font-normal text-neutral-900 md:text-sm">
					Recuerda que también puedes terminar de gestionar tu cobertura a
					través de nuestro Whatsapp.
				</p>
			</div>

			{/* Footer buttons - Desktop only */}
			<div className="hidden w-full flex-col items-center justify-between gap-3 md:flex md:flex-row md:gap-0">
				<button
					type="button"
					onClick={onBack}
					className="font-parkinsans order-2 text-sm leading-6 font-normal text-blue-500 underline transition-colors hover:text-blue-600 md:order-1 md:text-base md:leading-[30px]"
				>
					Volver a cotizar
				</button>
				<Link to="/" className="order-1 w-full md:order-2 md:w-auto">
					<Button
						variant="primary"
						appearance="solid"
						size="l"
						className="w-full overflow-hidden rounded-xl px-6 py-4 md:w-auto"
					>
						Ir al inicio
					</Button>
				</Link>
			</div>

			{/* Mobile footer - Fixed at bottom */}
			<div className="fixed right-0 bottom-0 left-0 z-10 border-t border-neutral-400 bg-white px-6 py-3 md:hidden">
				<div className="flex items-center justify-between">
					<button
						type="button"
						onClick={onBack}
						className="font-parkinsans text-sm leading-5 font-normal text-blue-500 underline transition-colors hover:text-blue-600"
					>
						Volver a cotizar
					</button>
					<Link to="/">
						<Button
							variant="primary"
							appearance="solid"
							size="m"
							className="gap-2 rounded-xl px-4 py-3"
						>
							Ir al inicio
						</Button>
					</Link>
				</div>
			</div>
		</div>
	)
}
